import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  BrainCircuit,
  Plus,
  Radio,
  Users,
  Zap,
} from "lucide-react";
import { formatAppTime } from "../../../utils/appTimeFormat";

/**
 * Prediction Overview — exchange-floor landing view for the tips desk.
 * Pulse tiles, live board, and ranked fixture slips (P2P pit visual language).
 */

const KIND_META = {
  agree: { label: "Aligned", short: "AI + crowd", color: "#34d399" },
  split: { label: "Split", short: "AI vs crowd", color: "#fbbf24" },
  ai: { label: "AI only", short: "AI only", color: "#64B5F6" },
  crowd: { label: "Crowd only", short: "Crowd only", color: "#90CAF9" },
};

const isLive = (entry) => entry?.status?.tone === "live";

const deskStats = (fixtures = []) => {
  let aiFixtures = 0;
  let crowdFixtures = 0;
  let crowdTips = 0;
  let liveCount = 0;

  fixtures.forEach((f) => {
    if (f.ai) aiFixtures += 1;
    if (f.consensus?.count > 0) {
      crowdFixtures += 1;
      crowdTips += f.consensus.count;
    }
    if (isLive(f)) liveCount += 1;
  });

  const nextUp = fixtures
    .filter((f) => f.status?.tone === "upcoming" && f.ts)
    .sort((a, b) => a.ts - b.ts)[0];

  return {
    aiFixtures,
    crowdFixtures,
    crowdTips,
    liveCount,
    nextKick: nextUp?.ts
      ? formatAppTime(new Date(Number(nextUp.ts) * 1000))
      : null,
  };
};

const dotKind = (entry) => {
  const hasAI = Boolean(entry.ai);
  const hasCrowd = entry.consensus.count > 0;
  if (hasAI && hasCrowd) return entry.edge === "agree" ? "agree" : "split";
  if (hasAI) return "ai";
  return "crowd";
};

const TeamMark = ({ team }) => (
  <span className="pr-slip-logo" aria-hidden>
    {team?.logo ? (
      <img src={team.logo} alt="" />
    ) : (
      <span>{(team?.name || "?").slice(0, 1)}</span>
    )}
  </span>
);

const OverviewSlip = ({ entry, selected, onSelect, rank }) => {
  const home = entry.fixture?.teams?.home || {};
  const away = entry.fixture?.teams?.away || {};
  const kind = dotKind(entry);
  const meta = KIND_META[kind];
  const live = isLive(entry);
  const kick = entry.ts ? formatAppTime(new Date(Number(entry.ts) * 1000)) : "TBD";
  const hasAI = Boolean(entry.aiReadout?.pick);
  const hasCrowd = Boolean(entry.consensus?.topPick);
  const percent = entry.aiReadout?.percent;
  const hasSplit = Boolean(percent && (percent.home || percent.draw || percent.away));

  return (
    <button
      type="button"
      className={`pr-ov-top-slip ${selected ? "pr-ov-top-slip-on" : ""}`}
      onClick={() => onSelect?.(entry.fixtureId)}
      aria-pressed={selected}
    >
      <i
        className="pr-ov-top-slip-accent"
        style={{ background: meta.color }}
        aria-hidden
      />
      {rank != null ? <span className="pr-ov-top-slip-rank">{rank}</span> : null}
      <span className="pr-ov-top-slip-body">
        <span className="pr-ov-top-slip-line1">
          <em>{entry.league?.name || "League"}</em>
          <span aria-hidden>·</span>
          <em className={live ? "pr-ov-live-tag" : ""}>{live ? "LIVE" : kick}</em>
          <b style={{ color: meta.color }}>{meta.label}</b>
        </span>
        <span className="pr-ov-top-slip-match">
          <TeamMark team={home} />
          <strong>{home.name || "Home"}</strong>
          <i>vs</i>
          <strong>{away.name || "Away"}</strong>
          <TeamMark team={away} />
        </span>
        {hasSplit ? (
          <span className="pr-slip-splitbar" aria-hidden>
            <i style={{ width: `${percent.home}%` }} />
            <i style={{ width: `${percent.draw}%` }} />
            <i style={{ width: `${percent.away}%` }} />
          </span>
        ) : null}
        <span className="pr-ov-top-slip-picks">
          {hasAI ? (
            <span className="pr-slip-pick pr-slip-pick-ai">
              <BrainCircuit size={9} />
              <b>{entry.aiReadout.pick}</b>
            </span>
          ) : null}
          {hasCrowd ? (
            <span className="pr-slip-pick pr-slip-pick-crowd">
              <Users size={9} />
              <b>{entry.consensus.topPick}</b>
            </span>
          ) : null}
        </span>
      </span>
      <span className="pr-ov-top-slip-action">
        <span className="pr-slip-cta">{entry.signal}</span>
      </span>
    </button>
  );
};

const PulseTile = ({ icon: Icon, value, label, tone }) => (
  <div className={`pr-ov-stat${tone ? ` pr-ov-stat-${tone}` : ""}`}>
    {Icon ? (
      <span className="pr-ov-stat-icon" aria-hidden>
        <Icon size={12} />
      </span>
    ) : null}
    <strong>{value}</strong>
    <em>{label}</em>
  </div>
);

const PulseStrip = ({ fixtures, isFiltered }) => {
  const stats = useMemo(() => deskStats(fixtures), [fixtures]);

  return (
    <section className="pr-ov-pulse-strip" aria-label="Desk snapshot">
      <PulseTile
        icon={Activity}
        value={fixtures.length}
        label={isFiltered ? "Filtered" : "Fixtures"}
      />
      <PulseTile
        icon={BrainCircuit}
        value={stats.aiFixtures}
        label="With AI"
        tone="ai"
      />
      <PulseTile
        icon={Users}
        value={stats.crowdFixtures}
        label="Crowd"
        tone="crowd"
      />
      <PulseTile
        icon={Radio}
        value={stats.liveCount}
        label="In play"
        tone={stats.liveCount ? "live" : undefined}
      />
      <PulseTile value={stats.nextKick || "—"} label="Next kick" />
    </section>
  );
};

const FixtureBoard = ({ title, hint, icon: Icon, fixtures, selectedId, onSelect, dense, live }) => {
  if (!fixtures.length) return null;

  return (
    <section className="pr-ov-slip-panel" aria-label={title}>
      <div className="pr-ov-slip-panel-head">
        <i
          className={`pr-ov-slip-panel-accent ${live ? "pr-ov-slip-panel-accent-live" : "pr-ov-slip-panel-accent-hot"}`}
          aria-hidden
        />
        <Icon size={13} aria-hidden />
        <span>{title}</span>
        <em>{hint}</em>
      </div>
      <div className={`pr-ov-top-list ${dense ? "pr-ov-top-list-dense" : ""}`}>
        {fixtures.map((entry, i) => (
          <OverviewSlip
            key={entry.fixtureId}
            entry={entry}
            rank={dense ? null : i + 1}
            selected={selectedId === entry.fixtureId}
            onSelect={onSelect}
          />
        ))}
      </div>
    </section>
  );
};

const PredictionOverview = ({
  fixtures = [],
  totals,
  selectedId,
  onSelect,
  isAuthenticated = false,
}) => {
  const isFiltered = fixtures.length !== (totals?.fixtures ?? fixtures.length);

  const live = useMemo(
    () => [...fixtures].filter(isLive).sort((a, b) => b.signal - a.signal).slice(0, 6),
    [fixtures]
  );
  const liveTotal = fixtures.filter(isLive).length;

  const upcoming = useMemo(
    () =>
      [...fixtures]
        .filter((f) => f.status?.tone === "upcoming")
        .sort((a, b) => (a.ts || 0) - (b.ts || 0))
        .slice(0, 4),
    [fixtures]
  );

  const top = useMemo(
    () => [...fixtures].sort((a, b) => b.signal - a.signal).slice(0, 6),
    [fixtures]
  );

  const createTo = isAuthenticated ? "/app/predictions/create" : "/app/login";
  const createState = isAuthenticated ? undefined : { from: "/app/predictions/create" };

  return (
    <section className="pr-room-detail pr-ov" aria-label="Prediction overview">
      <div className="pr-room-scroll">
        <header className="pr-ov-head">
          <div className="pr-title-slip pr-ov-title-slip">
            <div className="pr-title-slip-mid">
              <p className="pr-title-slip-eyebrow">Bond Tips · Desk</p>
              <div className="pr-title-slip-row">
                <h1>Overview</h1>
                <span className="pr-title-slip-pill">{fixtures.length} fixtures</span>
                {liveTotal ? (
                  <span className="pr-title-slip-pill pr-ov-live-pill">{liveTotal} live</span>
                ) : null}
              </div>
              <p className="pr-ov-head-sub">
                AI signals and crowd tips on the same fixtures — open a match to
                compare, unlock, or post your own call.
              </p>
            </div>
            <div className="pr-ov-head-actions">
              <Link to={createTo} state={createState} className="pr-ov-head-cta">
                <Plus size={14} aria-hidden />
                Post tip
              </Link>
            </div>
          </div>
        </header>

        <PulseStrip fixtures={fixtures} isFiltered={isFiltered} />

        {live.length ? (
          <FixtureBoard
            title="Live now"
            hint={liveTotal > live.length ? `${live.length} of ${liveTotal}` : `${live.length} in play`}
            icon={Radio}
            fixtures={live}
            selectedId={selectedId}
            onSelect={onSelect}
            dense
            live
          />
        ) : (
          <FixtureBoard
            title="Next up"
            hint="soonest kickoff"
            icon={Activity}
            fixtures={upcoming}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        )}

        <FixtureBoard
          title="Top slips"
          hint="strongest first"
          icon={Zap}
          fixtures={top}
          selectedId={selectedId}
          onSelect={onSelect}
          dense
        />
      </div>
    </section>
  );
};

export default PredictionOverview;
